/**
 * Hợp đồng caption dùng chung — browser Canvas + @napi-rs/canvas. Self-contained.
 *
 * MIRROR cấu trúc của `CaptionConfig` trong @vbee-holding/aivoice-shared-lib
 * (dubbing/caption), nguồn sự thật của shape này: tts-api validate/lưu và editor
 * drive UI theo nó, mà cả hai đều không cài renderer. Giữ local để không thêm
 * dependency — đúng cách shared-lib giữ mirror `Timeline` của renderer. Sửa file
 * này CÙNG LÚC với shared-lib; lệch nhau thì build consumer vỡ, đó là điểm phát
 * hiện. Lệch có chủ ý duy nhất: `layout.blockWidth` nullable, xem tại chỗ.
 *
 * ĐƠN VỊ: số không có đơn vị (fontSize, outline.width, shadow.offset*, mọi blur)
 * đo tại hệ quy chiếu cạnh ngắn 1080, khi vẽ nhân `min(W,H)/1080`; số theo %
 * (mọi opacity, mọi borderRadius, blockWidth, position, background.padding*)
 * KHÔNG nhân scale. Không dùng px ở đâu cả. Mọi opacity là 0-100.
 *
 * `position` và `blockWidth` lấy BÁT CHỮ (khung trừ lề an toàn) làm hệ quy chiếu;
 * bảng lề + công thức định vị ở /draw/captionLayout.ts vì cần biết size khung.
 */
import { InvalidInputError } from "./errors.js";

export interface RenderWord {
  text: string;
  start: number;
  end: number;
}

export interface RenderCaption {
  text: string;
  startTime: number;
  endTime: number;
  /** Rỗng = không có timestamp: BY_SPEECH không phân trang được, highlight tắt. */
  words: RenderWord[];
}

// ── Enum (khuôn WATERMARK_POSITIONS: mảng as const + kiểu suy ra) ────────────

/** `BOLD` KHÔNG cộng dồn vào `text.fontWeight` — weight của face thật là trục
 *  độc lập, face 900 không được faux-bold thêm lần nữa. Tầng vẽ dùng
 *  `max(fontWeight, 700)` khi có chip này. */
export const CAPTION_TEXT_FORMATS = ["BOLD", "ITALIC", "UNDERLINE", "STRIKETHROUGH"] as const;
export type CaptionTextFormat = (typeof CAPTION_TEXT_FORMATS)[number];

export const CAPTION_TEXT_CASES = ["NORMAL", "UPPERCASE", "LOWERCASE", "CAPITALIZE"] as const;
export type CaptionTextCase = (typeof CAPTION_TEXT_CASES)[number];

export const CAPTION_TEXT_ALIGNS = ["LEFT", "CENTER", "RIGHT"] as const;
export type CaptionTextAlign = (typeof CAPTION_TEXT_ALIGNS)[number];

/** ALL_TEXT vẽ trọn khối một lần (dòng không vừa bát chữ bị bỏ); BY_SPEECH gom
 *  từ thành trang maxLines × maxWords và vẽ trang chứa từ đang đọc.
 *  `one-word-at-a-time` cũ = BY_SPEECH với maxLines 1 / maxWords 1. */
export const CAPTION_REVEAL_MODES = ["ALL_TEXT", "BY_SPEECH"] as const;
export type CaptionRevealMode = (typeof CAPTION_REVEAL_MODES)[number];

// ── Dải giá trị — đồng bộ vbee-tts-api/src/constants/captionTemplate.js ──────
// Số ngoài dải bị KẸP, không ném: một style lệch dải vẫn nên render chứ không
// giết job export 80 phút. Mọi lần kẹp báo ra qua `adjusted`.

export const CAPTION_RANGES = {
  position: { min: 0, max: 100 },
  fontSize: { min: 20, max: 500 },
  fontWeight: { min: 100, max: 900 },
  opacity: { min: 0, max: 100 },
  blur: { min: 0, max: 30 }, // thang riêng, KHÔNG phải %
  borderRadius: { min: 0, max: 100 },
  outlineWidth: { min: 0, max: 20 },
  shadowOffset: { min: -100, max: 100 },
  backgroundPaddingX: { min: 0, max: 1000 }, // tới 1000% để nền phủ/tràn chiều ngang khung
  backgroundPaddingY: { min: 0, max: 150 },
  blockWidth: { min: 10, max: 100 },
  maxLines: { min: 1, max: 10 }, // quá 10 dòng thì khối chắc chắn cao hơn bát chữ
  maxWords: { min: 1, max: 20 },
} as const;

// ── 7 nhóm config, dạng đã resolve ──────────────────────────────────────────

/** Vị trí khối trong bát chữ theo neo tỉ lệ: điểm ở x% bề rộng khối trùng điểm
 *  ở x% bề rộng bát chữ. Là VỊ TRÍ chứ không phải "% lệch" — 0 mép trái, 50 giữa,
 *  100 mép phải; ba chip Trái/Giữa/Phải chỉ là ba giá trị đó, giá trị giữa hợp lệ. */
export interface CaptionPosition {
  x: number;
  y: number;
}

export interface CaptionText {
  fontFamily: string;
  fontWeight: number;
  /** `.woff2` của đúng face đó. Renderer KHÔNG tải font (network thuộc caller);
   *  ở đây để DB/API/preview/export dùng chung một shape. */
  fontUrl: string;
  fontSize: number;
  formats: CaptionTextFormat[];
  textCase: CaptionTextCase;
  color: string; // #RRGGBB; alpha nằm ở `opacity` chứ không trong mã màu
  /** % — chỉ phủ NÉT CHỮ; viền và bóng mang opacity riêng của chúng. */
  opacity: number;
  /** Căn lề BÊN TRONG khối; không làm đổi vị trí khối. */
  align: CaptionTextAlign;
}

export interface CaptionLayout {
  revealMode: CaptionRevealMode;
  /** % bề rộng bát chữ. shared-lib khai bắt buộc (cả 8 style seed đều đặt), ở
   *  đây nullable vì chỉ tầng vẽ biết tỷ lệ khung để chọn default 80/90/85 —
   *  renderer là chặng cuối và phải chịu được config thiếu trường. */
  blockWidth: number | null;
  maxLines: number; // chỉ dùng khi BY_SPEECH
  maxWords: number; // chỉ dùng khi BY_SPEECH; hiệu dụng = MIN(giá trị này, số từ vừa khối)
}

export interface CaptionShadow {
  enabled: boolean;
  color: string;
  opacity: number;
  blur: number;
  offsetX: number;
  offsetY: number;
}

/** Không có `enabled`: `width = 0` đã là tắt. */
export interface CaptionOutline {
  width: number;
  color: string;
  opacity: number;
}

export interface CaptionBackground {
  enabled: boolean;
  color: string;
  opacity: number;
  blur: number; // độ mờ MÉP nền, không phải backdrop blur
  /** Mép chữ → mép khung nền, % của `fontSize`. Nở ra ngoài và KHÔNG tham gia
   *  định vị (hộp mà công thức vị trí đo là hộp chữ), nên ở position 0/100 nền
   *  lấn lề an toàn và với padding lớn có thể tràn khung — có chủ ý. */
  paddingX: number;
  paddingY: number;
  borderRadius: number; // % nửa cạnh ngắn hộp nền
}

export interface CaptionHighlight {
  /** Karaoke theo giọng đọc; cần `RenderCaption.words`. */
  enabled: boolean;
  textColor: string; // #RRGGBB; alpha nằm ở `textOpacity`
  /** % — riêng của từ đang đọc, độc lập với `text.opacity` của phần còn lại. */
  textOpacity: number;
  /** ĐÈ LÊN nền của khối chứ không thay thế nó. */
  backgroundColor: string;
  backgroundOpacity: number; // 0 = chỉ đổi màu chữ
  borderRadius: number; // % nửa cạnh ngắn HỘP HIGHLIGHT, không phải hộp nền khối
}

/** Dạng tầng vẽ nhận — mọi trường đã có giá trị. */
export interface ResolvedCaptionStyle {
  position: CaptionPosition;
  text: CaptionText;
  layout: CaptionLayout;
  shadow: CaptionShadow;
  outline: CaptionOutline;
  background: CaptionBackground;
  highlight: CaptionHighlight;
}

/** Dạng API trả về: nhóm nào cũng có thể vắng và nhóm có mặt cũng có thể thưa
 *  (style tắt nền chỉ gửi `background: { enabled: false }`). */
export interface CaptionStyle {
  position?: Partial<CaptionPosition>;
  text?: Partial<CaptionText>;
  layout?: Partial<CaptionLayout>;
  shadow?: Partial<CaptionShadow>;
  outline?: Partial<CaptionOutline>;
  background?: Partial<CaptionBackground>;
  highlight?: Partial<CaptionHighlight>;
}

export const CAPTION_STYLE_DEFAULTS: ResolvedCaptionStyle = {
  position: { x: 50, y: 100 },
  text: {
    // Family rỗng làm chuỗi ctx.font không parse được và ctx giữ font trước đó.
    fontFamily: "sans-serif",
    fontWeight: 400,
    fontUrl: "",
    fontSize: 48,
    formats: [],
    textCase: "NORMAL",
    color: "#FFFFFF",
    opacity: 100,
    align: "CENTER",
  },
  layout: { revealMode: "ALL_TEXT", blockWidth: null, maxLines: 2, maxWords: 8 },
  shadow: { enabled: false, color: "#000000", opacity: 100, blur: 0, offsetX: 0, offsetY: 0 },
  outline: { width: 0, color: "#000000", opacity: 100 },
  background: {
    enabled: false, color: "#000000", opacity: 75, blur: 0,
    paddingX: 50, paddingY: 25, borderRadius: 20,
  },
  highlight: {
    enabled: false, textColor: "#1E164E", textOpacity: 100,
    backgroundColor: "#FFD60A", backgroundOpacity: 100, borderRadius: 20,
  },
};

// ── resolveCaptionStyle ─────────────────────────────────────────────────────

export interface ResolveCaptionStyleResult {
  style: ResolvedCaptionStyle;
  /** Đường dẫn các trường renderer phải sửa để dùng được (số bị kẹp, màu sai
   *  định dạng), ví dụ `"text.fontSize"`. Compositor biến thành FidelityNote;
   *  preview bỏ qua được. Rỗng = config nhận nguyên văn. */
  adjusted: string[];
}

const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;

type Range = { readonly min: number; readonly max: number };

/** Vắng ⇒ default, không ghi nhận (input thưa là hợp đồng). Sai kiểu ⇒ default,
 *  có ghi nhận. Ngoài dải ⇒ kẹp, có ghi nhận. */
function num(value: unknown, def: number, range: Range, field: string, adjusted: string[]): number {
  if (value === undefined || value === null) return def;
  if (typeof value !== "number" || !Number.isFinite(value)) {
    adjusted.push(field);
    return def;
  }
  const clamped = Math.min(range.max, Math.max(range.min, value));
  if (clamped !== value) adjusted.push(field);
  return clamped;
}

/** Làm tròn — trường ĐẾM (dòng/từ) đánh chỉ số nên thập phân vô nghĩa. */
function count(value: unknown, def: number, range: Range, field: string, adjusted: string[]): number {
  const n = num(value, def, range, field, adjusted);
  const rounded = Math.round(n);
  if (rounded !== n && !adjusted.includes(field)) adjusted.push(field);
  return rounded;
}

/** Trường DUY NHẤT mà "vắng" khác "default": phải giữ `null` để tầng vẽ chọn
 *  theo tỷ lệ khung. Giá trị rác cũng về `null`, không về một con số bịa. */
function blockWidthOf(value: unknown, adjusted: string[]): number | null {
  if (value === undefined || value === null) return null;
  if (typeof value !== "number" || !Number.isFinite(value)) {
    adjusted.push("layout.blockWidth");
    return null;
  }
  const { min, max } = CAPTION_RANGES.blockWidth;
  const clamped = Math.min(max, Math.max(min, value));
  if (clamped !== value) adjusted.push("layout.blockWidth");
  return clamped;
}

function family(value: unknown, def: string, field: string, adjusted: string[]): string {
  if (value === undefined || value === null) return def;
  const trimmed = typeof value === "string" ? value.trim() : "";
  if (!trimmed) {
    adjusted.push(field);
    return def;
  }
  return trimmed;
}

/** HEX sai định dạng ⇒ default, tránh `rgba(NaN,…)` xuống canvas. */
function color(value: unknown, def: string, field: string, adjusted: string[]): string {
  if (value === undefined || value === null) return def;
  if (typeof value !== "string" || !HEX_COLOR_RE.test(value)) {
    adjusted.push(field);
    return def;
  }
  return value.toUpperCase();
}

/** Ghi nhận khi sai kiểu (`"true"` từ form chưa parse): một hiệu ứng tắt lặng lẽ
 *  là loại lệch chuẩn khó truy nhất. */
function bool(value: unknown, def: boolean, field: string, adjusted: string[]): boolean {
  if (value === undefined || value === null) return def;
  if (typeof value !== "boolean") {
    adjusted.push(field);
    return def;
  }
  return value;
}

/** Enum NÉM chứ không rơi về default: giá trị lạ nghĩa là caller và renderer
 *  hiểu khác nhau về ngữ nghĩa, vẽ tiếp bằng ngữ nghĩa sai tệ hơn dừng lại. */
function enumOf<T extends string>(
  value: unknown, allowed: readonly T[], def: T, field: string,
): T {
  if (value === undefined || value === null) return def;
  if (typeof value !== "string" || !allowed.includes(value as T)) {
    throw new InvalidInputError(
      `${field} must be one of ${allowed.join(" | ")} (got ${JSON.stringify(value)})`,
    );
  }
  return value as T;
}

function textFormats(value: unknown, field: string): CaptionTextFormat[] {
  if (value === undefined || value === null) return [];
  if (!Array.isArray(value)) {
    throw new InvalidInputError(`${field} must be an array of ${CAPTION_TEXT_FORMATS.join(" | ")}`);
  }
  const out: CaptionTextFormat[] = [];
  for (const item of value) {
    if (typeof item !== "string" || !CAPTION_TEXT_FORMATS.includes(item as CaptionTextFormat)) {
      throw new InvalidInputError(
        `${field} must contain only ${CAPTION_TEXT_FORMATS.join(" | ")} (got ${JSON.stringify(item)})`,
      );
    }
    if (!out.includes(item as CaptionTextFormat)) out.push(item as CaptionTextFormat);
  }
  return out;
}

/** Điền default cho config thưa + kẹp số về dải. Gọi MỘT LẦN mỗi style rồi giữ
 *  lại: drawCaption chạy mỗi khung trong vòng rAF. */
const GROUP_KEYS = [
  "position", "text", "layout", "shadow", "outline", "background", "highlight",
] as const satisfies readonly (keyof CaptionStyle)[];

export function resolveCaptionStyle(style?: CaptionStyle): ResolveCaptionStyleResult {
  if (style !== undefined && (style === null || typeof style !== "object")) {
    throw new InvalidInputError(`caption style must be an object (got ${JSON.stringify(style)})`);
  }
  const d = CAPTION_STYLE_DEFAULTS;
  const r = CAPTION_RANGES;
  const adjusted: string[] = [];
  const s = style ?? {};

  // Trường sai kiểu được ghi vào `adjusted`, nhưng cả NHÓM sai kiểu
  // (`{ background: "dark" }`) thì optional chaining nuốt im: mọi trường rơi về
  // default mà không ai biết. Ghi nhận ở đây để tầng gọi còn phát cảnh báo.
  for (const group of GROUP_KEYS) {
    const value = s[group];
    if (value !== undefined && (value === null || typeof value !== "object")) {
      adjusted.push(group);
    }
  }

  const position: CaptionPosition = {
    x: num(s.position?.x, d.position.x, r.position, "position.x", adjusted),
    y: num(s.position?.y, d.position.y, r.position, "position.y", adjusted),
  };

  const text: CaptionText = {
    fontFamily: family(s.text?.fontFamily, d.text.fontFamily, "text.fontFamily", adjusted),
    fontWeight: num(s.text?.fontWeight, d.text.fontWeight, r.fontWeight, "text.fontWeight", adjusted),
    fontUrl: typeof s.text?.fontUrl === "string" ? s.text.fontUrl : d.text.fontUrl,
    fontSize: num(s.text?.fontSize, d.text.fontSize, r.fontSize, "text.fontSize", adjusted),
    formats: textFormats(s.text?.formats, "text.formats"),
    textCase: enumOf(s.text?.textCase, CAPTION_TEXT_CASES, d.text.textCase, "text.textCase"),
    color: color(s.text?.color, d.text.color, "text.color", adjusted),
    opacity: num(s.text?.opacity, d.text.opacity, r.opacity, "text.opacity", adjusted),
    align: enumOf(s.text?.align, CAPTION_TEXT_ALIGNS, d.text.align, "text.align"),
  };

  const layout: CaptionLayout = {
    revealMode: enumOf(s.layout?.revealMode, CAPTION_REVEAL_MODES, d.layout.revealMode, "layout.revealMode"),
    blockWidth: blockWidthOf(s.layout?.blockWidth, adjusted),
    maxLines: count(s.layout?.maxLines, d.layout.maxLines, r.maxLines, "layout.maxLines", adjusted),
    maxWords: count(s.layout?.maxWords, d.layout.maxWords, r.maxWords, "layout.maxWords", adjusted),
  };

  const shadow: CaptionShadow = {
    enabled: bool(s.shadow?.enabled, d.shadow.enabled, "shadow.enabled", adjusted),
    color: color(s.shadow?.color, d.shadow.color, "shadow.color", adjusted),
    opacity: num(s.shadow?.opacity, d.shadow.opacity, r.opacity, "shadow.opacity", adjusted),
    blur: num(s.shadow?.blur, d.shadow.blur, r.blur, "shadow.blur", adjusted),
    offsetX: num(s.shadow?.offsetX, d.shadow.offsetX, r.shadowOffset, "shadow.offsetX", adjusted),
    offsetY: num(s.shadow?.offsetY, d.shadow.offsetY, r.shadowOffset, "shadow.offsetY", adjusted),
  };

  const outline: CaptionOutline = {
    width: num(s.outline?.width, d.outline.width, r.outlineWidth, "outline.width", adjusted),
    color: color(s.outline?.color, d.outline.color, "outline.color", adjusted),
    opacity: num(s.outline?.opacity, d.outline.opacity, r.opacity, "outline.opacity", adjusted),
  };

  const background: CaptionBackground = {
    enabled: bool(s.background?.enabled, d.background.enabled, "background.enabled", adjusted),
    color: color(s.background?.color, d.background.color, "background.color", adjusted),
    opacity: num(s.background?.opacity, d.background.opacity, r.opacity, "background.opacity", adjusted),
    blur: num(s.background?.blur, d.background.blur, r.blur, "background.blur", adjusted),
    paddingX: num(s.background?.paddingX, d.background.paddingX, r.backgroundPaddingX, "background.paddingX", adjusted),
    paddingY: num(s.background?.paddingY, d.background.paddingY, r.backgroundPaddingY, "background.paddingY", adjusted),
    borderRadius: num(s.background?.borderRadius, d.background.borderRadius, r.borderRadius, "background.borderRadius", adjusted),
  };

  const highlight: CaptionHighlight = {
    enabled: bool(s.highlight?.enabled, d.highlight.enabled, "highlight.enabled", adjusted),
    textColor: color(s.highlight?.textColor, d.highlight.textColor, "highlight.textColor", adjusted),
    textOpacity: num(s.highlight?.textOpacity, d.highlight.textOpacity, r.opacity, "highlight.textOpacity", adjusted),
    backgroundColor: color(s.highlight?.backgroundColor, d.highlight.backgroundColor, "highlight.backgroundColor", adjusted),
    backgroundOpacity: num(s.highlight?.backgroundOpacity, d.highlight.backgroundOpacity, r.opacity, "highlight.backgroundOpacity", adjusted),
    borderRadius: num(s.highlight?.borderRadius, d.highlight.borderRadius, r.borderRadius, "highlight.borderRadius", adjusted),
  };

  return {
    style: { position, text, layout, shadow, outline, background, highlight },
    adjusted,
  };
}

export interface CaptionFrame {
  caption: RenderCaption | null;
  style: ResolvedCaptionStyle;
  time: number;
  canvasWidth: number;
  canvasHeight: number;
}
